// Client-side analysis for the "AHT by intent" reporting sub-sections --
// pure functions over getHandleTimeReport's `intents` (per-intent team-wide
// ranking) and `agentIntent` (the agent x intent cross-tab) fields. No fetch,
// no day-bucketing -- both inputs are already whole-selected-range snapshots,
// same grain as `intents`/`agents`, unlike `agentDaily`.

// Below this many contacts, one unusually long (or short) contact swings an
// agent's average too much to call it a real signal -- same reasoning this
// report's median/p90 stats exist for elsewhere.
export const MIN_OUTLIER_COUNT = 5

// An agent's avg is flagged once it's this much above the intent's team avg.
// A first-cut number, not empirically derived -- expect to retune once this
// has run against real data.
export const OUTLIER_THRESHOLD_PCT = 0.25

// intents: getHandleTimeReport().intents. Returns { [intentKey]: avgMs }.
export function intentBaselines(intents) {
  return Object.fromEntries((intents ?? []).map((i) => [i.key, i.avgMs]))
}

// How far an agent's avg sits above their intent's team avg, as a fraction
// (0.25 = 25% slower). Null with no team baseline to compare against.
function pctOver(avgMs, baselineMs) {
  if (!baselineMs) return null
  return (avgMs - baselineMs) / baselineMs
}

// Every (agent, intent) row annotated with its team baseline and pctOver,
// unfiltered -- the drill-down table's source (shows every agent for the one
// intent it filters to, not just outliers).
export function agentIntentWithBaseline({ agentIntent, intents }) {
  const baselines = intentBaselines(intents)
  return (agentIntent ?? []).map((row) => ({
    ...row,
    teamAvgMs: baselines[row.intent] ?? null,
    pctOver: pctOver(row.avgMs, baselines[row.intent]),
  }))
}

// The flat "coaching opportunities" list across every intent at once: rows
// meeting BOTH the minimum sample size and the outlier threshold, worst
// (highest pctOver) first.
export function outlierRows({ agentIntent, intents, minCount = MIN_OUTLIER_COUNT, thresholdPct = OUTLIER_THRESHOLD_PCT }) {
  return agentIntentWithBaseline({ agentIntent, intents })
    .filter((r) => r.count >= minCount && r.pctOver != null && r.pctOver >= thresholdPct)
    .sort((a, b) => b.pctOver - a.pctOver)
}
